import React, { useEffect, useState } from "react";

import { isCampaignManufacturer } from "../../utils/Utils";
import { toDateStringIgnoringTimezone } from "../../utils/TimeUtil";
import { DashboardService } from "../../service/DashboardService";
import { getDashboardProfile, PROFILE_KEYS } from "./dashboardProfiles";
import { buildActions, isEmptyState } from "./dashboardModel";
import DashboardHero from "./DashboardHero";
import AttentionQueue from "./AttentionQueue";
import DashboardStats from "./DashboardStats";
import OverviewCompetitor from "./OverviewCompetitor";
import OverviewSites from "./OverviewSites";
import { Loading } from "../../utils/InlineComponents";
import { AppContext } from "../../HomePage";
import { CampaignContext } from "../../App";
import "./dashboard-ux.css";

const Overview = () => {
  const { t, setError } = React.useContext(AppContext);
  const { selectedCampaign } = React.useContext(CampaignContext);

  const [overview, setOverview] = useState({});
  const [unauthorized, setUnauthorized] = useState([]);
  const [loading, setLoading] = useState(false);

  const dashboardService = new DashboardService();
  const isManufacturer = isCampaignManufacturer(selectedCampaign);
  const profile = getDashboardProfile(selectedCampaign);
  // Market-search campaigns have no "my price" to judge against, so the plain
  // product-count hero and the cheapest/cheaper-than-me seller lists add no
  // signal. Drop them (and the all-clear attention message) for that profile,
  // keeping only genuinely actionable items (e.g. stock-outs). The hero is still
  // shown for a brand-new (0-product) market campaign so its onboarding CTA
  // survives — only the "N products tracked" verdict is suppressed.
  const isMarket = profile.key === PROFILE_KEYS.MARKET;
  // The unauthorized COUNT for the hero + attention card comes from the overview
  // (overview.total_unauthorized = products with unauthorized_count > 0), so it
  // matches the product-list ?unauthorized=true filter exactly. The separate
  // getUnauthorizedSellers fetch below is only for the sellers-panel breakdown.
  const showHero = !isMarket || isEmptyState(profile, overview);
  const showAttention = !isMarket || buildActions(profile, overview).length > 0;

  useEffect(() => { search(); }, [selectedCampaign]);

  const search = () => {
    setLoading(true);
    dashboardService.getOverview(selectedCampaign.id, toDateStringIgnoringTimezone(new Date())).then((data) => {
      setOverview(data || {});
      setLoading(false);
    }).catch((error) => { setError(error); setError(null); setLoading(false); });

    // Reset first so a manufacturer -> manufacturer switch never flashes the previous
    // campaign's unauthorized count in the hero/attention card while the new fetch runs.
    setUnauthorized([]);
    if (isManufacturer) {
      dashboardService.getUnauthorizedSellers(selectedCampaign.id, toDateStringIgnoringTimezone(new Date())).then((data) => {
        setUnauthorized(data || []);
      }).catch((error) => { setError(error); setError(null); });
    }
  };

  return (
    <React.Fragment>
      <div className="dux-identity">
        <span className="pux-section-title">{selectedCampaign.name}</span>
      </div>

      {loading ? (
        <div className="p-col-12" style={{ textAlign: "center" }}><Loading /></div>
      ) : (
        <React.Fragment>
          {showHero && <DashboardHero profile={profile} overview={overview} t={t} />}
          {showAttention && (
            <React.Fragment>
              <div className="pux-section-title" style={{ marginTop: 8 }}>{t('dashboard.attention', 'Needs attention')}</div>
              <AttentionQueue profile={profile} overview={overview} t={t} />
            </React.Fragment>
          )}
          <DashboardStats overview={overview} t={t} />
          {!isMarket && <OverviewCompetitor overview={overview} profile={profile} isManufacturer={isManufacturer} unauthorizedData={unauthorized} />}
          <OverviewSites isMarket={isMarket} isManufacturer={isManufacturer} />
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

export default Overview;
